import { Box, Button, Link, Popover } from '@mui/material'
import { Column } from 'shared/ui/Table/types'
import React, { useMemo, useState } from 'react'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { altTextSourceDataAtom, byUrlDataAtom } from './atoms'

export function useImageColumn(onImgLoadError?: (url: string) => void) {
  const allUrlData = useAtomValue(byUrlDataAtom)
  return useMemo(() => {
    return {
      title: 'Image',
      width: 150,
      rowContent(url: string) {
        const { altText } = allUrlData[url] ?? {}
        return (
          <ImgPreview
            src={url}
            altText={altText}
            onLoadError={onImgLoadError}
          />
        )
      },
    }
  }, [allUrlData, onImgLoadError])
}

export function useCountColumn() {
  const allUrlData = useAtomValue(byUrlDataAtom)
  return useMemo(() => {
    return {
      title: 'Count',
      width: 150,
      rowContent(url: string) {
        const { nodes, leaves } = allUrlData[url]
        const display = []
        if (nodes.length) display.push(`N: ${nodes.length}`)
        if (leaves.length) display.push(`L: ${leaves.length}`)
        return display.join('; ')
      },
    }
  }, [allUrlData])
}

export function useSelectColumn(
  title: string,
  onSelect: (item: string) => void,
  buttonLabel = 'X',
) {
  return useMemo(() => {
    return {
      title,
      width: 50,
      rowContent(param: string) {
        return <Button onClick={() => onSelect(param)}>{buttonLabel}</Button>
      },
    }
  }, [title, onSelect, buttonLabel])
}

export function useAltTextColumn() {
  const allUrlData = useAtomValue(byUrlDataAtom)
  const altTextSource = useAtomValue(altTextSourceDataAtom)

  return useMemo(() => {
    return {
      title: 'Alt Text',
      width: 500,
      rowContent(url: string) {
        const generated = altTextSource[url]
        const { altText } = allUrlData[url]
        return (
          <Box>
            <Box>
              <strong>Existing: </strong>
              {altText}
            </Box>
            <Box color="purple">
              <strong>New Alt Text: </strong>
              {generated?.altText}
            </Box>
          </Box>
        )
      },
    }
  }, [allUrlData, altTextSource])
}

export function useCantIncludeColumns() {
  const countCol = useCountColumn()
  return useMemo(
    () => [
      countCol,
      {
        title: 'Reason',
        width: 150,
        rowContent(url: string) {
          return url.startsWith('data') ? 'Data Src' : 'Broken Link'
        },
      },
      {
        title: 'URL',
        width: 150,
        rowContent(url: string) {
          return url
        },
      },
    ],
    [countCol],
  )
}

export function useWithoutAltColumns(
  onSelect: (item: string) => void,
  onSelectLabel: string,
  onImgLoadError?: (url: string) => void,
): Column<string>[] {
  const imgCol = useImageColumn(onImgLoadError)
  const countCol = useCountColumn()
  const selCol = useSelectColumn(onSelectLabel, onSelect)
  return useMemo(() => [imgCol, countCol, selCol], [imgCol, countCol, selCol])
}

export function useWithAltTextColumns({
  setSelectedUrl,
  onToRemoveAltText,
  onToKeepAltText,
}: {
  setSelectedUrl: (url: string) => void
  onToKeepAltText: (url: string) => void
  onToRemoveAltText: (url: string) => void
}) {
  const imgCol = useImageColumn()
  const altCol = useAltTextColumn()
  const countCol = useCountColumn()
  const selectCol = useSelectColumn('Select', setSelectedUrl, 'S')
  const withSelect = useSelectColumn('Remove Alt Text', onToKeepAltText, 'R')
  const removeSelect = useSelectColumn('Cancel', onToRemoveAltText, 'C')

  return useMemo(() => {
    return {
      withColumns: [imgCol, altCol, countCol, selectCol, withSelect],
      toRemoveColumns: [imgCol, altCol, countCol, selectCol, removeSelect],
    }
  }, [imgCol, altCol, countCol, selectCol, withSelect, removeSelect])
}

export function useGeneratedColumns(
  onSelect: (url: string) => void,
  onSelectTitle: string,
): Column<string>[] {
  const imgCol = useImageColumn()
  const altCol = useAltTextColumn()
  const countCol = useCountColumn()
  const selCol = useSelectColumn(onSelectTitle, onSelect)
  return useMemo(
    () => [imgCol, altCol, countCol, selCol],
    [imgCol, altCol, countCol, selCol],
  )
}

export function ImgPreview({
  src,
  altText,
  onLoadError,
}: {
  src: string
  altText?: string | null
  onLoadError?: (url: string) => void
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>()
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handlePopoverClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  return (
    <Box
      padding="5px"
      component={Link}
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
    >
      <img
        src={src}
        alt={altText ?? 'missing'}
        width="50px"
        onError={() => onLoadError?.(src)}
      />
      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        onClose={handlePopoverClose}
        anchorEl={anchorEl}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <img src={src} alt={altText ?? 'missing'} width="500px" />
      </Popover>
    </Box>
  )
}
